import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { BookOpen, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);

  // Scroll to top when component mounts and check for messages
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Show verification message if coming from registration
    if (location.state?.message) {
      setShowVerificationMessage(true);
      // Clear the state so the message doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        // Check if this was a lifetime offer flow
        if (location.state?.lifetimeOffer) {
          toast.success('Welcome back! Redirecting to claim your lifetime access...');
          navigate('/dashboard?purchase=lifetime');
        } else {
          toast.success('Welcome back!');
          navigate('/dashboard');
        }
      } else {
        console.error('Login failed:', result.error);
        toast.error(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login exception:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      // Check if Google API is loaded
      if (!window.google || !window.google.accounts) {
        toast.error('Google Sign-In is not available. Please refresh the page.');
        setLoading(false);
        return;
      }

      // Use the simpler popup approach instead of One Tap
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID,
        scope: 'email profile openid',
        callback: handleGoogleResponse,
      });

      // Request the token
      client.requestAccessToken();
      
    } catch (error) {
      console.error('Google Sign-In initialization error:', error);
      toast.error('Google Sign-In failed. Please try again.');
      setLoading(false);
    }
  };

  const handleGoogleResponse = async (response) => {
    try {
      if (response.error) {
        console.error('Google OAuth error:', response.error);
        toast.error('Google sign-in was cancelled or failed');
        setLoading(false);
        return;
      }

      // Get user info using the access token
      const userInfoResponse = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${response.access_token}`);
      const userInfo = await userInfoResponse.json();

      // Create a mock ID token with user info for our backend
      const mockIdToken = btoa(JSON.stringify({
        sub: userInfo.id,
        email: userInfo.email,
        name: userInfo.name,
        picture: userInfo.picture,
        email_verified: userInfo.verified_email
      }));

      const result = await loginWithGoogle(mockIdToken);

      if (result.success) {
        toast.success('Welcome! You have been signed in successfully.');
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Google sign-in failed');
      }
    } catch (error) {
      console.error('Google authentication error:', error);
      toast.error('Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SEOHead 
        title="Login to Your Account - AI BookMoney"
        description="Sign in to your AI BookMoney account. Continue creating and selling books with our AI-powered KDP publishing tools. Access your dashboard and start earning today."
        keywords="login, sign in, AI BookMoney account, user login, publishing dashboard"
        canonical="/login"
        ogTitle="Login to AI BookMoney - Continue Publishing"
        ogDescription="Sign in to continue using AI-powered KDP publishing tools and grow your book business."
        ogImage="/ai-manuscript-demo.png"
        ogUrl="/login"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Login - AI BookMoney",
          "description": "Sign in to AI BookMoney account",
          "url": "https://aibookmoney.com/login"
        }}
      />
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center space-x-2 mb-8">
            <img src="/logo.svg" alt="AI BookMoney" className="h-12 w-auto" />
            <span className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              AI BookMoney
            </span>
          </Link>
          <h2 className="text-3xl font-bold text-white">
            Sign in to your account
          </h2>
          <p className="mt-2 text-gray-300">
            Continue creating amazing books with AI
          </p>
        </div>

        {/* Verification Message */}
        {showVerificationMessage && (
          <div className="mb-6 bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-blue-300 mb-1">Check Your Email</h4>
              <p className="text-blue-200 text-sm">
                We've sent you a verification email. Please click the link in your email to verify your account before logging in.
              </p>
              <button 
                onClick={() => setShowVerificationMessage(false)}
                className="text-blue-400 hover:text-blue-300 text-sm underline mt-2"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/20">
          {/* Google Sign-In Button */}
          <div className="mb-6">
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full bg-gray-800 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-semibold border border-gray-600 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Continue with Google</span>
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-900 text-gray-400">or</span>
            </div>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link to="/request-password-reset" className="text-purple-400 hover:text-purple-300 transition-colors">
                  Forgot your password?
                </Link>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-300">
              Don't have an account?{' '}
              <Link 
                to="/register" 
                className="text-purple-400 hover:text-purple-300 font-semibold transition-colors"
              >
                Sign up for free
              </Link>
            </p>
          </div>

        </div>

        {/* Back to Home */}
        <div className="text-center">
          <Link 
            to="/" 
            className="text-gray-400 hover:text-purple-400 transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;